// Per-event attribution capture for the new-schema event stream.
//
// Captures UTMs (`utm_source`, `utm_medium`, `utm_campaign`, `utm_term`,
// `utm_content`) and GCLID from the URL on first read, and persists them
// in sessionStorage so subsequent page navigations within the same session
// keep the values intact. Falls back to the `_gcl_aw` cookie for GCLID
// when the URL doesn't carry it (matches Google's own conversion-linker
// persistence pattern).
//
// When the URL carries no UTM source AND we have no stored attribution,
// falls back to classifying `document.referrer` into a synthetic
// (utm_source, utm_medium) pair — standard last-touch attribution.
// Handles both web hostnames (google.com → organic) and the mobile-app
// referrer scheme browsers emit on Android (android-app://com.facebook.katana
// → facebook/social). A non-empty referrer that we can't classify becomes
// (referrer-unparseable, referral) rather than collapsing to Direct, so
// we preserve the "they came from somewhere" signal in attribution reports.
//
// These fields ride on every event the widget emits to sGTM, so Dan's
// dashboards can break out conversions by source / medium / campaign at
// the event level rather than relying solely on GA4's session-level
// attribution off the `dl` URL parser.
//
// Legacy `booking · step=N` events get their attribution via
// `legacy-dispatch.ts::captureReferralOnce()` — that path is unchanged.
// The two paths are intentionally parallel: legacy uses
// `referral_method_primary/secondary` (mapped from utm_source/utm_medium)
// for compatibility with the existing CC funnel; new schema uses the
// canonical `utm_*` + `gclid` event params.

const STORAGE_KEY = 'cc_widget_attribution';

const UTM_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
] as const;

export interface Attribution {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
  gclid?: string;
  // gbraid + wbraid are Google's iOS conversion-tracking identifiers,
  // sent on Google Ads clicks when gclid would be unreliable under Apple's
  // App Tracking Transparency rules. iPad/iPhone visits frequently arrive
  // with one of these instead of (or alongside) gclid; GA4 understands
  // both natively, so we capture and forward them with the same lifecycle
  // as gclid. Critical for CC since ~70% of traffic is iOS Safari.
  gbraid?: string;
  wbraid?: string;
}

let cached: Attribution | null = null;

function readUtmsFromUrl(): Attribution {
  if (typeof window === 'undefined') return {};
  const params = new URLSearchParams(window.location.search);
  const out: Attribution = {};
  for (const key of UTM_KEYS) {
    const v = params.get(key);
    if (v) out[key] = v;
  }
  const gclid = params.get('gclid');
  if (gclid) out.gclid = gclid;
  const gbraid = params.get('gbraid');
  if (gbraid) out.gbraid = gbraid;
  const wbraid = params.get('wbraid');
  if (wbraid) out.wbraid = wbraid;
  return out;
}

function readGclidFromCookie(): string | undefined {
  if (typeof document === 'undefined') return undefined;
  // Google sets _gcl_aw on the conversion-linker path; format is
  // `GCL.<timestamp>.<encoded_gclid>`. We extract the third part.
  const m = document.cookie.match(/(?:^|;\s*)_gcl_aw=([^;]+)/);
  if (!m) return undefined;
  const parts = decodeURIComponent(m[1]).split('.');
  if (parts.length < 3) return undefined;
  const candidate = parts.slice(2).join('.');
  return candidate.length > 0 ? candidate : undefined;
}

// --- Referrer classification ---------------------------------------------
//
// Base-name tokens we treat as known search engines / social platforms.
// Matched against the extracted "registrable-domain stem" of the referrer
// hostname (e.g., "google" from "www.google.co.uk", "facebook" from
// "m.facebook.com"). Conservative list — only platforms that are real
// drivers of traffic. Adding an entry is cheap; misclassifying isn't.

const SEARCH_ENGINES = new Set([
  'google',
  'bing',
  'yahoo',
  'duckduckgo',
  'brave',
  'ecosia',
  'yandex',
  'baidu',
  'startpage',
  'qwant',
  'kagi',
]);

const SOCIAL_PLATFORMS = new Set([
  'facebook',
  'instagram',
  'twitter',
  'x',
  'linkedin',
  'reddit',
  'tiktok',
  'youtube',
  'pinterest',
  'snapchat',
  'threads',
  'nextdoor',
]);

// Exact-host overrides for short link shorteners and platform subdomains
// that don't follow the base-name heuristic. Maps a full lowercase
// hostname to the canonical source name.
const SOCIAL_HOSTS = new Map<string, string>([
  ['t.co', 'twitter'],
  ['lnkd.in', 'linkedin'],
  ['youtu.be', 'youtube'],
  ['m.youtube.com', 'youtube'],
  ['fb.me', 'facebook'],
  ['l.facebook.com', 'facebook'],
  ['lm.facebook.com', 'facebook'],
  ['l.instagram.com', 'instagram'],
]);

// AI assistants / chat-driven referrals. Treated as `referral` medium
// rather than `organic` — GA4 has no standard `ai` channel, and these
// are not search engines in the traditional indexed-results sense.
const AI_HOSTS = new Map<string, string>([
  ['chatgpt.com', 'chatgpt'],
  ['chat.openai.com', 'chatgpt'],
  ['perplexity.ai', 'perplexity'],
  ['www.perplexity.ai', 'perplexity'],
  ['claude.ai', 'claude'],
  ['gemini.google.com', 'gemini'],
  ['bard.google.com', 'gemini'],
  ['copilot.microsoft.com', 'copilot'],
  ['you.com', 'you.com'],
  ['poe.com', 'poe'],
]);

// Hostnames the widget itself runs under. A referrer from one of these
// is internal navigation, not an attribution signal — fall through and
// let sessionStorage (or the lack thereof) decide.
const INTERNAL_HOSTS = new Set([
  'clubchampion.com',
  'www.clubchampion.com',
  'booking.clubchampion.com',
  'booking-staging.clubchampion.com',
  'staging.clubchampion.com',
]);

// Android emits a non-HTTP referrer scheme (`android-app://<package>/`)
// when the user comes from an installed app's in-app browser. These don't
// parse to a useful hostname so we match on the full referrer prefix.
// The set is intentionally small — only the apps that meaningfully drive
// CC traffic. iOS doesn't use this scheme; iOS in-app browsers either
// pass the underlying web URL as the referrer or strip it entirely.
const ANDROID_APP_REFERRERS: Array<{
  prefix: string;
  source: string;
  medium: string;
}> = [
  { prefix: 'android-app://com.google.android.googlequicksearchbox', source: 'google', medium: 'organic' },
  { prefix: 'android-app://com.facebook.katana', source: 'facebook', medium: 'social' },
  { prefix: 'android-app://com.facebook.lite', source: 'facebook', medium: 'social' },
  { prefix: 'android-app://com.instagram.android', source: 'instagram', medium: 'social' },
  { prefix: 'android-app://com.twitter.android', source: 'twitter', medium: 'social' },
  { prefix: 'android-app://com.zhiliaoapp.musically', source: 'tiktok', medium: 'social' },
  { prefix: 'android-app://com.snapchat.android', source: 'snapchat', medium: 'social' },
  { prefix: 'android-app://com.linkedin.android', source: 'linkedin', medium: 'social' },
  { prefix: 'android-app://com.reddit.frontpage', source: 'reddit', medium: 'social' },
  { prefix: 'android-app://com.pinterest', source: 'pinterest', medium: 'social' },
  { prefix: 'android-app://com.google.android.gm', source: 'gmail', medium: 'email' },
];

// Country-code SLD tokens that appear as the second-to-last hostname
// segment in CCTLD-style hostnames (e.g., "co" in "google.co.uk"). When
// present, the registrable stem is parts[-3] not parts[-2].
const CCTLD_SLDS = new Set(['co', 'com', 'org', 'net', 'gov', 'ac', 'edu']);

function extractBaseName(hostname: string): string {
  const stripped = hostname.replace(/^www\./, '');
  const parts = stripped.split('.');
  if (parts.length < 2) return stripped;
  const secondLast = parts[parts.length - 2];
  if (parts.length >= 3 && CCTLD_SLDS.has(secondLast)) {
    return parts[parts.length - 3];
  }
  return secondLast;
}

/**
 * Classify document.referrer into a synthetic (source, medium) pair.
 *
 * Returns `{}` when there's no usable signal (no referrer at all, OR a
 * same-domain internal navigation). Returns a populated pair when we
 * can identify the referrer, including a `(referrer-unparseable, referral)`
 * fallback for non-empty referrers that don't match any known pattern —
 * better to bucket as a generic referral than to silently drop into Direct.
 *
 * Conservative ordering: exact-host matches win over base-name matches,
 * mobile-app patterns are checked before the URL parse path.
 */
function classifyReferrer(): { utm_source?: string; utm_medium?: string } {
  if (typeof document === 'undefined') return {};
  const ref = document.referrer;
  if (!ref) return {};

  // Mobile-app referrers (android-app://…) — check the literal prefix first.
  for (const entry of ANDROID_APP_REFERRERS) {
    if (ref.startsWith(entry.prefix)) {
      return { utm_source: entry.source, utm_medium: entry.medium };
    }
  }

  let parsed: URL;
  try {
    parsed = new URL(ref);
  } catch {
    // Genuine parse failure — non-empty but malformed. Preserve the
    // "they came from somewhere" signal as a generic referral so
    // attribution reports can flag growth and add patterns if needed.
    return { utm_source: 'referrer-unparseable', utm_medium: 'referral' };
  }

  const host = parsed.hostname.toLowerCase();
  if (!host) {
    // Parsed cleanly but no hostname (uncommon non-app scheme we don't
    // recognize). Same logic as parse-failure: preserve the signal.
    return { utm_source: 'referrer-unparseable', utm_medium: 'referral' };
  }
  if (INTERNAL_HOSTS.has(host)) return {};

  // Exact-host special cases (t.co, chatgpt.com, gemini.google.com, etc.).
  const aiHit = AI_HOSTS.get(host);
  if (aiHit) return { utm_source: aiHit, utm_medium: 'referral' };
  const socialHostHit = SOCIAL_HOSTS.get(host);
  if (socialHostHit) return { utm_source: socialHostHit, utm_medium: 'social' };

  // Base-name match for the canonical platforms.
  const base = extractBaseName(host);
  if (SEARCH_ENGINES.has(base)) return { utm_source: base, utm_medium: 'organic' };
  if (SOCIAL_PLATFORMS.has(base)) return { utm_source: base, utm_medium: 'social' };

  // Recognized hostname shape, but not a known platform — bucket as an
  // external referral with the registrable hostname as the source.
  return { utm_source: host.replace(/^www\./, ''), utm_medium: 'referral' };
}

function readFromStorage(): Attribution | null {
  if (typeof window === 'undefined' || !window.sessionStorage) return null;
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as Attribution;
  } catch {
    return null;
  }
}

function writeToStorage(attr: Attribution): void {
  if (typeof window === 'undefined' || !window.sessionStorage) return;
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(attr));
  } catch {
    // sessionStorage quota / private mode — silent fallback to in-memory.
  }
}

/**
 * Returns the active attribution snapshot for this session.
 *
 * Resolution order:
 *   1. URL params (highest precedence — a new attributed visit overrides
 *      a stored value from an earlier landing)
 *   2. _gcl_aw cookie (for gclid only, if not in URL)
 *   3. sessionStorage (carried forward from an earlier emit this session)
 *   4. document.referrer (synthetic source/medium when nothing above
 *      identified a source — catches direct-to-widget visits from
 *      external pages with no UTM tagging)
 *
 * Result is cached in memory after the first call and written back to
 * sessionStorage so subsequent navigations within the session keep it.
 */
export function getAttribution(): Attribution {
  if (cached) return cached;
  const fromUrl = readUtmsFromUrl();
  const fromStorage = readFromStorage() ?? {};

  // Fresh-attribution detection. When the current URL declares a new
  // attribution event — either via a UTM source or a click ID — we treat
  // it as a brand new attributed visit and DISCARD prior stored values
  // entirely. Otherwise click IDs leak across attribution boundaries: a
  // morning Google Ads click leaves a gclid in sessionStorage, an afternoon
  // GBP click writes utm_source=gbp but inherits the old gclid, and GA4
  // ends up seeing `source=gbp + gclid=<google-ads-id>` for the rest of the
  // session — over-crediting Google Ads for conversions actually driven by
  // later channels. The same logic applies in reverse for organic visits
  // that follow a paid one.
  //
  // Merge semantics:
  //   - Fresh attributed visit (URL has utm_source OR gclid/gbraid/wbraid)
  //     → use ONLY the URL values; storage is discarded.
  //   - Internal navigation (URL has none of the above) → carry storage
  //     forward unchanged; URL contributes nothing.
  const urlHasFreshAttribution = !!(
    fromUrl.utm_source ||
    fromUrl.gclid ||
    fromUrl.gbraid ||
    fromUrl.wbraid
  );
  const merged: Attribution = urlHasFreshAttribution
    ? { ...fromUrl }
    : { ...fromStorage };

  // GCLID fallback to cookie. Skipped when the URL declares a fresh
  // non-Google attribution (gbp, facebook, email, etc.) — Google's
  // _gcl_aw conversion-linker cookie persists for ~90 days after any
  // Google Ads click, so without this guard we'd attach a stale gclid
  // to a brand-new non-paid click and over-credit Google Ads for
  // conversions actually driven by other channels.
  const urlDeclaresNonGoogleSource = !!(
    fromUrl.utm_source && fromUrl.utm_source !== 'google'
  );
  if (!merged.gclid && !urlDeclaresNonGoogleSource) {
    const fromCookie = readGclidFromCookie();
    if (fromCookie) merged.gclid = fromCookie;
  }
  // Referrer fallback. Only fires when we still have no identified
  // source/medium after URL + storage. URL UTMs and stored UTMs both
  // take precedence — referrer is strictly a last-touch backstop.
  if (!merged.utm_source && !merged.utm_medium) {
    const fromReferrer = classifyReferrer();
    if (fromReferrer.utm_source) merged.utm_source = fromReferrer.utm_source;
    if (fromReferrer.utm_medium) merged.utm_medium = fromReferrer.utm_medium;
  }
  // Final fallback: synthesize explicit Direct when nothing — URL,
  // storage, cookies, or referrer — identified a source. GA4 should
  // auto-classify zero-signal visits as `(direct)/(none)` but in
  // practice (cross-domain self-referrals from the SF confirmation
  // sub-property, app-stripped referrers, session-continuation edge
  // cases) the auto-classification fails and these land in Unassigned.
  // Emitting the explicit Direct sentinel pair on widget events forces
  // GA4 to bucket them into the Direct channel, which is the honest
  // ad-attribution answer for a visit with no signal at any layer.
  if (!merged.utm_source && !merged.utm_medium) {
    merged.utm_source = '(direct)';
    merged.utm_medium = '(none)';
  }
  cached = merged;
  if (Object.keys(merged).length > 0) writeToStorage(merged);
  return merged;
}

// Eagerly capture attribution on module load.
//
// The widget bundle is included on (nearly) every page of clubchampion.com,
// but `getAttribution()` is only invoked when an event actually fires — and
// many users land with UTMs, browse for a while without opening the widget,
// then navigate into the booking flow on a deeper page where the host's SPA
// has stripped the original landing-page query string. Without this call,
// the URL UTMs are gone by the time the first event fires, and we attribute
// the eventual booking to Direct (or to whatever internal referrer carries
// the user the last hop).
//
// Running on module load freezes the source/medium/campaign at the earliest
// possible moment in the session and writes it to sessionStorage, where it
// survives subsequent navigations within the origin.
//
// Wrapped in try/catch for paranoia — analytics setup must never throw into
// the host page and break the rest of the bundle.
if (typeof window !== 'undefined') {
  try {
    getAttribution();
  } catch {
    /* never block widget boot on attribution capture */
  }
}
